import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { useQuery } from "@tanstack/react-query";
import type { SessionUser } from "@/lib/auth";

/**
 * useIdentify — binds PostHog identity to the authenticated user.
 *
 * Reads the /api/auth/me cache (populated by AuthGuard / MobileAuthGuard)
 * and calls posthog.identify() when a user is present, or posthog.reset()
 * when logged out. Only id, role, and clientId are sent — never PII (ANLY-01).
 *
 * Safe when VITE_POSTHOG_KEY is unset: posthog will be null/undefined and
 * the guard below prevents any calls.
 */
export function useIdentify(): void {
  const posthog = usePostHog();
  const { data: user } = useQuery<SessionUser | null>({
    queryKey: ["/api/auth/me"],
    staleTime: 60_000,
    // No queryFn — reads from cache populated by AuthGuard / MobileAuthGuard.
    // TanStack Query deduplicates by queryKey so this is always in sync.
  });

  useEffect(() => {
    if (!posthog) return;

    if (user !== null && user !== undefined) {
      // Identify with non-PII properties only (ANLY-01 requirement).
      posthog.identify(String(user.id), {
        role: user.role,
        clientId: user.clientId ?? undefined,
      });
      // Set Sentry user context for error attribution (ERR-10).
      Sentry.setUser({ id: String(user.id), role: user.role });
    } else {
      // user is null (logged out) or undefined (loading with no cache) —
      // reset to prevent cross-session identity pollution (ANLY-02).
      posthog.reset();
      // Clear Sentry user context so subsequent errors are not attributed
      // to the previous session (ERR-10).
      Sentry.setUser(null);
    }
  }, [user?.id, posthog]);
}
